import { CertificateOrientation } from '@zeus/index'

/** Taille max du logo uploadé en data-URI (base64 stocké en DB), comme le branding. */
const MAX_LOGO_BYTES = 512 * 1024

/** Taille max du squelette PDF (aligné sur la limite serveur). */
const MAX_SKELETON_BYTES = 3 * 1024 * 1024

/** Placeholders injectables dans le corps du modèle, affichés en aide. */
export const PLACEHOLDERS = [
  '{{firstName}}',
  '{{lastName}}',
  '{{moduleTitle}}',
  '{{sessionName}}',
  '{{objectives}}',
  '{{completedAt}}',
  '{{score}}',
  '{{totalTime}}',
  '{{appName}}',
]

export type FormState = {
  name: string
  title: string
  body: string
  signature: string
  showScore: boolean
  showDate: boolean
  showTotalTime: boolean
  emailOnUnlock: boolean
  orientation: CertificateOrientation
  logo: string
  marginTop: number
  marginBottom: number
  marginLeft: number
  marginRight: number
}

export const EMPTY_FORM: FormState = {
  name: '',
  title: '',
  body: '',
  signature: '',
  showScore: true,
  showDate: true,
  showTotalTime: false,
  emailOnUnlock: false,
  orientation: CertificateOrientation.LANDSCAPE,
  logo: '',
  marginTop: 64,
  marginBottom: 64,
  marginLeft: 60,
  marginRight: 60,
}

/** Un modèle tel que l'API le rend, les champs absents ramenés à leur défaut. */
export function formFromTemplate(template: Partial<Record<keyof FormState, unknown>>): FormState {
  const fields = Object.fromEntries(
    Object.entries(template).filter(([key, value]) => key in EMPTY_FORM && value != null),
  )
  return { ...EMPTY_FORM, ...fields } as FormState
}

/**
 * Squelette PDF : `undefined` = inchangé (le PDF stocké, jusqu'à 3 Mo, n'est pas
 * rechargé), `''` = retrait demandé, data-URI = nouveau fichier.
 */
export type SkeletonChange = string | undefined

/** Ce que l'API reçoit du formulaire. */
export function toTemplateInput(form: FormState, skeleton: SkeletonChange) {
  return {
    ...form,
    name: form.name || undefined,
    title: form.title || undefined,
    body: form.body || undefined,
    // Chaînes vides envoyées telles quelles : elles effacent la signature, ou le
    // logo (repli sur celui de l'application), côté API.
    ...(skeleton !== undefined ? { skeletonPdf: skeleton } : {}),
  }
}

/** Le formulaire diffère-t-il du modèle enregistré ? */
export function isFormDirty(form: FormState, saved: FormState, skeleton: SkeletonChange): boolean {
  return skeleton !== undefined || (Object.keys(form) as (keyof FormState)[]).some(key => form[key] !== saved[key])
}

/** Clé du message d'erreur d'un logo refusé, ou `null` s'il convient. */
export function logoFileError(file: File): string | null {
  if (!/^image\/(?:png|jpeg)$/.test(file.type)) return 'certificates.logo.typeError'
  return file.size > MAX_LOGO_BYTES ? 'certificates.logo.sizeError' : null
}

/** Clé du message d'erreur d'un fond refusé, ou `null` s'il convient. */
export function skeletonFileError(file: File): string | null {
  if (file.type !== 'application/pdf') return 'certificates.skeleton.typeError'
  return file.size > MAX_SKELETON_BYTES ? 'certificates.skeleton.sizeError' : null
}
